import { IEntity } from 'shared/models/Entity';
import { ILocation } from 'shared/models/Location';
import { PayPeriodType, WeekDayEnum } from 'shared/models/PaySettings';
import { QuantityType } from 'shared/models/sheet/Sheet';
import { IUserInfo } from 'shared/models/User';
import { IClientStatuses } from 'store/components/auth/authModels';
import { Nullable } from 'types/types';

export enum AvailableFormConfiguration {
    Time = 'time',
    Expense = 'expense'
}

export enum AvailableTableConfiguration {
    TimeExpense = 'time_expense',
    Time = 'time',
    Expense = 'expense',
    TimeDetail = 'time_detail',
    ExpenseDetail = 'expense_detail',
    EmployeeExpenseSheet = 'employee_expense_sheet',
    EmployeeTimeSheet = 'employee_time_sheet',
    ManagerExpenseSheet = 'manager_expense_sheet',
    ManagerTimeSheet = 'manager_time_sheet',
}

export enum AvailableDetailConfiguration {
    EditTimeEntry = 'edit_time_entry',
    EditExpenseEntry = 'edit_expense_entry',
    EmployeeTimeSheet = 'employee_time_sheet',
    ManagerTimeSheet = 'manager_time_sheet',
    EmployeeExpenseSheet = 'employee_expense_sheet',
    ManagerExpenseSheet = 'manager_expense_sheet',
}

export enum AvailableTotalConfiguration {
    DaySheetTotal = 'day_sheet_total',
    SheetsTotal = 'sheets_total',
    TimeSheetDetail = 'time_sheet_detail',
    TimeSheetDetailDay = 'time_sheet_detail_day',
}

export interface IThemeConfiguration {
    primary_color: string;
    logo_url: string;
}

export interface IClient extends IEntity {
    name: string;
    short_name: string;
    created_at?: string;
    prism_client_id: string;
    avionte_company_id?: string;
    has_offers?: boolean;
}

export interface IEmployeeClientInfo {
    client?: IClient;
    has_employee_profile?: boolean;
    has_offer_letter?: boolean;
    has_active_assignments?: boolean;
}

export enum EntrySlug {
    Day = 'day',
    Task = 'task',
    Assignment = 'assignment_project',
    JobNumber = 'job_number',
    Activity = 'activity',
    Notes = 'notes',
    ZipCode = 'zip_code',
    ScaZone = 'sca_zone',
    Location = 'location',
    Position = 'position',
    Department = 'department',
    Time = 'time',
    TimeIn = 'time_in',
    TimeOut = 'time_out',
}

export interface IInputConfiguration {
    slug: EntrySlug;
    visible: boolean;
    placeholder: string;
    default_value?: string;
}

export type InputFields = Record<EntrySlug, IInputConfiguration>;

export interface IInputsConfiguration {
    time: InputFields;
    expense: InputFields;
}

export interface IClientsThemes {
    [idClients: string]: IThemeConfigurationWrapper;
}

export type TablesConfiguration = Record<AvailableTableConfiguration, IColumnConfiguration<ColumnSlugs>[]>;
export type DetailsConfiguration = Record<AvailableDetailConfiguration, {
    info: Array<IDetailConfiguration<DetailSlugs>>;
    title?: IDetailConfiguration<DetailSlugs>;
}>;
export type FiltersConfiguration = Record<AvailableFiltersConfiguration, FiltersConfigurationBySlug>;

export enum SheetTotalSlug {
    Time = 'time',
    Expense = 'expense',
    Completes = 'completes',
    PerDiem = 'per-diem',
    Files = 'files',
    HolidayTime = 'holiday-time',
    PtoTime = 'pto',
    RegularTime = 'total_reg',
    OverTime = 'ot',
    DoubleTime = 'dt',
    Break = 'break',
}

export enum TotalSlug {
    TrailingDoc = 'trailing-doc',
    NewLoan = 'new_loan',
    Files = 'files',
    TotalReg = 'total_reg',
    OverTime = 'ot',
    DoubleTime = 'dt',
    Break = 'break',
    Holidays = 'holidays',
    Pto = 'pto',
    Psl = 'psl',
    Completes = 'completes',
    Time = 'time',
    Expense = 'expense',
}

export interface ITotalFieldsConfiguration<Slug> {
    slug: Slug;
    unitLabel?: string;
}

export interface ISheetTotalConfiguration extends ITotalFieldsConfiguration<SheetTotalSlug> {

}

export interface ITotalConfiguration extends ITotalFieldsConfiguration<TotalSlug> {

}

export interface ITotalsConfiguration {
    [AvailableTotalConfiguration.SheetsTotal]: ITotalConfiguration[]
    [AvailableTotalConfiguration.DaySheetTotal]: ISheetTotalConfiguration[]
    [AvailableTotalConfiguration.TimeSheetDetail]: ITotalConfiguration[]
    [AvailableTotalConfiguration.TimeSheetDetailDay]: ISheetTotalConfiguration[]
}

export interface IFieldsConfiguration {
    inputs: IInputsConfiguration;
    tables: TablesConfiguration;
    detail: DetailsConfiguration;
    filters: FiltersConfiguration;
    totals: ITotalsConfiguration;
}

export interface IClientConfiguration extends Omit<IClientConfigurationBackend, keyof IFieldsConfiguration> {}

export type ActivityType = QuantityType.TIME
| QuantityType.TIME_IN_OUT
| QuantityType.TIME_BREAK
| QuantityType.TIME_IN_OUT_BREAK
| QuantityType.FILE

export interface ITimeTracking {
    activity_types: ActivityType[];
    lock_past_dates?: boolean;
}

export enum ApproversFromFields {
    FromAssignment= 'assignment',
    FromJobNumber= 'job_number',
}

export interface ITimeAndPayClientConfiguration {
    id: string;
    timeTracking: ITimeTracking;
    approvalLevels: number;
    approversFrom: ApproversFromFields;
    hasJobNumberConfiguration: boolean;
    isGovernmentContract: boolean;
    scaEnabled: boolean;
    allowFutureDayEntry: boolean;
    allowTimeEntryWithoutEndTime: boolean;
    expenseReceiptRequiredDollars: number;
    mileageRate: number;
    allowTimeSheetGrouping: boolean;
    enableOfferLetterEmployeeNumber: boolean;
}

export interface IPatchAppSettingRequest {
    approval_levels: number;
    SCA: {
        enabled: boolean;
    } | undefined;
    allow_future_day_entry: boolean | undefined;
    allow_time_entry_without_end_time: boolean;
    expense_receipt_required_dollars: number;
    enable_offer_letter_employee_number: boolean | undefined;
    mileage_rate: string;
    ui_sheet_grouping: ISheetGroupingConfiguration | undefined;
    time_tracking: ITimeTracking;
}

export interface IEnabled {
    enabled: boolean;
}

export interface ICustomFieldsHierarchyConfiguration extends IEnabled {
    max_depth: number;
}

export interface ITravelExpensesConfiguration extends IEnabled {}

export interface IScaConfiguration extends IEnabled {}

export interface IImpersonationConfig extends IEnabled {}

export interface IPayRangesConfig extends IEnabled {}

export interface ISheetGroupingConfiguration {
    enabled: boolean;
    approval_grouping_keys: Array<keyof ISheetGroupKey>;
}

export interface ICcpConfiguration {
    enabled: boolean;
    custom_field_department_id: string;
    client_role_for_regional_director_display_id: string;
    client_regional_dir_match_limitation_field_names?: string[];
}

export interface IConfigurationExternalLinks {
    name: string;
    link: string;
    enabled: boolean;
}

export interface IClientConfigurationBackend {
    inputs: Array<{ type: AvailableFormConfiguration; fields: Array<IInputConfiguration> }>;
    tables: Array<{ type: AvailableTableConfiguration; columns: Array<IColumnConfiguration<ColumnSlugs>> }>;
    details: Array<{
        type: AvailableDetailConfiguration;
        info: Array<IDetailConfiguration<DetailSlugs>>;
        title?: IDetailConfiguration<DetailSlugs>;
    }>;
    filters: Array<{ type: AvailableFiltersConfiguration; fields: Array<IFiltersConfiguration> }>;
    totals: Array<{
        type: AvailableTotalConfiguration;
        info: Array<ITotalFieldsConfiguration<TotalSlug | SheetTotalSlug>>
    }>;
    time_tracking: ITimeTracking;
    approvers_from?: ApproversFromFields;
    is_government_contract: boolean;
    allow_future_day_entry: boolean;
    allow_time_entry_without_end_time: boolean;
    eaf_approver_page_enabled: boolean;
    sheet_entry_group_key: string[];
    approval_levels: number;
    custom_fields_hierarchy: ICustomFieldsHierarchyConfiguration;
    travel_expenses: ITravelExpensesConfiguration;
    missing_sheets_generation_enabled?: boolean;
    SCA: IScaConfiguration;
    ui_sheet_grouping: ISheetGroupingConfiguration;
    enable_offer_letter_employee_number: boolean | undefined;
    mileage_rate?: string;
    impersonation?: IImpersonationConfig;
    pay_ranges?: IPayRangesConfig;
    zoho_analytics_dashboard_permalink?: Nullable<string>;
    credit_card_portal_link?: Nullable<string>;
    credit_card_page_config?: ICcpConfiguration;
    hide_subassignments?: boolean;
    eaf_retro?: IEnabled;
    expense_receipt_required_dollars?: number;
    external_links?: IConfigurationExternalLinks[];
}

export interface IFieldsConfigurationWrapper {
    id: string;
    client_id: string;
    configuration: IClientConfigurationBackend;
}

export interface IThemeConfigurationWrapper {
    id: string;
    client_id: string;
    theme: IThemeConfiguration;
}

export enum AvailableFiltersConfiguration {
    EmployeeEntries = 'employee_entries',
    EmployeeSheets = 'employee_sheets',
    ManagerSheets = 'manager_sheets',
    SheetDetails = 'sheet_details',
}

export enum FilterSlug {
    Location = 'location',
    Position = 'position',
    LocationByAssignment = 'location_by_assignment',
    PositionByAssignment = 'position_by_assignment',
    Area = 'area',
    Assignment = 'assignment',
    Task = 'task',
    Activity = 'activity',
    JobNumber = 'job_number',
    Employee = 'employee',
    PayPeriod = 'pay_period',
}

export interface IColumnConfiguration<TableSlugs extends ColumnSlugs> {
    slug: TableSlugs;
    placeholder: string;
    additionalData?: any;
}

export interface IDetailConfiguration<DetailSlug extends DetailSlugs> {
    slug: DetailSlug;
    placeholder: string;
    additionalData?: any;
}

export interface IFiltersConfiguration {
    slug: FilterSlug;
    placeholder: string;
}

export type FiltersConfigurationBySlug = Record<FilterSlug, IFiltersConfiguration>;

export enum SheetColumnSlug {
    CustomField = 'custom_field',
    SCA = 'sca',
    Employee = 'employee',
    PayPeriod = 'pay_period',
    Area = 'area',
    Hours = 'hours',
    Amount = 'amount',
    Receipt = 'receipt',
    Id = 'id',
    Approver = 'approver',
    Position = 'position',
    Location = 'location',
    Department = 'department',
    JobNumber = 'job_number',
    RegularHours = 'regular_hours',
    Overtime1Hours = 'overtime_1_hours',
    Overtime2Hours = 'overtime_2_hours',
    HolidayHours = 'holiday_hours',
    PtoHours = 'pto',
}

export enum SheetDetailSlug {
    SheetId = 'sheet_id',
    PayPeriod = 'pay_period',
    Approver = 'approver',
    Position = 'position',
    Location = 'location',
    Department = 'department',
    Area = 'area',
    Employee = 'employee',
    PayRate = 'pay_rate',
    Notes = 'notes',
    BatchId = 'prism_batch_id',
    DeliveryOrder = 'delivery_order',
    JobsiteLocation = 'jobsite_location',
    JobNumber = 'job_number',
    DealNumber = 'deal_number',
}
export enum EntryColumnSlug {
    Activity = 'activity',
    Position = 'position',
    Location = 'location',
    ZipCode = 'zip_code',
    Receipt = 'receipt',
    Amount = 'amount',
    AmountReadonly = 'amount_readonly',
    Assignment = 'assignment',
    AssignmentProject = 'assignment_project',
    Task = 'task',
    Notes = 'notes',
    Quantity = 'quantity',
    Rate = 'rate',
    PayRate = 'pay_rate',
    Hours = 'hours',
    Time = 'time',
    Department = 'department',
    JobNumber = 'job_number',
    TimeReadOnly = 'time_readonly',
    Break = 'break',
    BreakReadOnly = 'break_readonly',
    CustomField = 'custom_field',
    SCA = 'sca',
    Employee = 'employee',
}

export interface IPayrollInfo {
    client_id: string;
    pay_date: string;
}

export type ColumnSlugs = EntryColumnSlug | SheetColumnSlug
export type DetailSlugs = SheetDetailSlug

export enum PrismCorporationType {
    L = 'L',
    S = 'S',
    C = 'C',
    I = 'I',
    P = 'P',
    A = 'A',
    M = 'M',
    R = 'R',
    V = 'V',
    B = 'B',
    J = 'J',
    T = 'T',
    U = 'U',
    H = 'H',
    N = 'N',
    O = 'O'
}

export const PrismCorporationTypeTitle: Record<PrismCorporationType, string> = {
    [PrismCorporationType.L]: 'Limited Liability Corporation',
    [PrismCorporationType.S]: 'S-Corporation',
    [PrismCorporationType.C]: 'C-Corporation',
    [PrismCorporationType.I]: 'Individual/Sole Proprieter',
    [PrismCorporationType.P]: 'Partnership',
    [PrismCorporationType.A]: 'Limited Partnership',
    [PrismCorporationType.M]: 'Limited Liability Partnership',
    [PrismCorporationType.R]: 'Professional Corporation',
    [PrismCorporationType.V]: 'Personal Service Corporation',
    [PrismCorporationType.B]: 'Board Owned',
    [PrismCorporationType.J]: 'Joint Venture',
    [PrismCorporationType.T]: 'Tax Exempt',
    [PrismCorporationType.U]: 'Trust',
    [PrismCorporationType.H]: 'Church Organization',
    [PrismCorporationType.N]: 'Not for Profit',
    [PrismCorporationType.O]: 'Other',
};

export interface IClientPaySettings {
    id: string;
    client_id: string;
    pay_period_type: PayPeriodType;    
    pay_period_start_day: WeekDayEnum;
    pay_period_end_day: WeekDayEnum;
    pay_period_start_date: string | null;
    pay_day: WeekDayEnum;
    created_at: string;
    updated_at: string;
    week_end_on_day: WeekDayEnum;
}

export interface IClientInfo extends IClient {
    short_name: string;
    name: string;
    employer_id: string;
    cost_center: string;
    status: IClientStatuses;
    status_date: string;
    service_type: string;
    main_location: ILocation;
    manager: IUserInfo | null;
    payroll_rep_user: IUserInfo | null;
    address: string;
    address2: string;
    city: string;
    state: string;
    country?: string;
    county: string;
    zip_code: string;
    contact_name: string;
    contact_title: string;
    contact_email: string;
    contact_phone: string;
    created_at?: string;
    prism_corporation_type?: PrismCorporationType;
    has_offers: boolean;
    has_assignments : boolean;
    avionte_company_id?: string;
}

export interface ISheetGroupKey {
    period_start: string;
    period_end: string;
}
